<!-- src/lib/components/ProjectOverview.svelte -->
<!--
  Project overview page — refinement #4. Shows project name,
  description, health badge, card counts by status, milestones,
  and recent activity. First tab in Tasks section.
-->
<script lang="ts">
  import { onDestroy } from 'svelte';
  import { pmStore } from '../stores/pm.svelte.ts';
  import { agentStore } from '../stores/agents.svelte.ts';
  import { api } from '../api.ts';
  import { shortDate } from '../format-time.ts';
  import StatusIcon from './StatusIcon.svelte';
  import EmptyState from './EmptyState.svelte';

  interface Milestone { id: string; title: string; due_date: string | null; status: string; }
  interface Goal { id: string; title: string; description: string | null; }

  let milestones = $state<Milestone[]>([]);
  let goals      = $state<Goal[]>([]);

  const project = $derived(
    pmStore.projects.find(p => p.id === pmStore.activeProjectId) ?? null
  );

  const linkedRepo = $derived(
    project?.repo_id ? pmStore.repos.find(r => r.id === project.repo_id) ?? null : null
  );

  let linkingRepo = $state(false);

  async function linkRepo(repoId: string | null) {
    if (!pmStore.activeProjectId) return;
    linkingRepo = true;
    try {
      const updated = await api<typeof project>(`/api/v1/projects/${pmStore.activeProjectId}`, {
        method: 'PATCH',
        body: JSON.stringify({ repo_id: repoId }),
      });
      if (updated) {
        const idx = pmStore.projects.findIndex(p => p.id === updated.id);
        if (idx !== -1) pmStore.projects[idx] = updated;
      }
    } catch (e) {
      console.warn('Failed to link repo:', e);
    } finally {
      linkingRepo = false;
    }
  }
  const board = $derived(
    pmStore.activeBoardId ? pmStore.boardDetails[pmStore.activeBoardId] : null
  );

  // Card counts per column (status)
  const statusCounts = $derived((() => {
    if (!board) return [];
    return board.columns.map(col => ({
      name: col.name,
      count: col.cards.length,
      color: col.color,
    }));
  })());

  const totalCards = $derived(statusCounts.reduce((s, c) => s + c.count, 0));
  const activeAgentCards = $derived((() => {
    if (!board) return [];
    const allCards = board.columns.flatMap((column) => column.cards);
    return agentStore.running
      .map((agent) => {
        const card = allCards.find((item) => item.agent_id === agent.agent_id);
        return card ? { agent, card } : null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  })());
  const overdueCards = $derived((() => {
    if (!board) return [];
    const doneColumn = board.columns.find((column) => /done|complete/.test(column.name.toLowerCase()));
    const now = Date.now();
    return board.columns
      .filter((column) => column.id !== doneColumn?.id)
      .flatMap((column) => column.cards)
      .filter((card) => (
        card.priority === 'urgent' &&
        !!card.due_date &&
        new Date(card.due_date).getTime() < now
      ))
      .slice(0, 6);
  })());
  let nowMs = $state(Date.now());
  const overviewTick = setInterval(() => { nowMs = Date.now(); }, 1000);

  onDestroy(() => clearInterval(overviewTick));

  function ovElapsed(startedAt: string): string {
    const ms = nowMs - new Date(startedAt).getTime();
    const seconds = Math.floor(Math.max(0, ms) / 1000);
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  }

  async function loadExtras() {
    if (!pmStore.activeProjectId) return;
    const pid = pmStore.activeProjectId;
    try {
      milestones = await api<Milestone[]>(`/api/v1/projects/${pid}/milestones`);
    } catch { milestones = []; }
    if (pmStore.activeWorkspaceId) {
      try {
        goals = await api<Goal[]>(`/api/v1/workspaces/${pmStore.activeWorkspaceId}/goals`);
      } catch { goals = []; }
    }
  }

  $effect(() => { if (pmStore.activeProjectId) void loadExtras(); });
</script>

{#if !project}
  <EmptyState icon="board" title="No project selected" subtitle="Pick or create project from sidebar." />
{:else}
  <div class="overview">
    <header class="ov-header">
      <h1>{project.name}</h1>
      {#if project.description}
        <p class="ov-desc">{project.description}</p>
      {/if}
      <div class="ov-meta">
        <span class="meta-item">Slug: <code>{project.slug}</code></span>
        <span class="meta-item">Created: {shortDate(project.created_at)}</span>
        <span class="meta-item">Cards: {totalCards}</span>
      </div>

      <div class="ov-repo-row">
        {#if linkedRepo}
          <span class="repo-badge">
            <span class="repo-icon">⎇</span>
            {linkedRepo.name}
          </span>
        {:else}
          <span class="repo-none">No repo linked</span>
        {/if}

        {#if pmStore.repos.length > 0}
          <select
            class="repo-link-select"
            value={project.repo_id ?? ''}
            disabled={linkingRepo}
            onchange={(e) => {
              const v = (e.currentTarget as HTMLSelectElement).value;
              void linkRepo(v === '' ? null : v);
            }}
          >
            <option value="">— Link repo —</option>
            {#each pmStore.repos as repo (repo.id)}
              <option value={repo.id}>{repo.name}</option>
            {/each}
          </select>
        {/if}
      </div>
    </header>

    {#if activeAgentCards.length > 0}
      <section class="ov-card mc-section">
        <h2>Active agents <span class="mc-count">{activeAgentCards.length}</span></h2>
        <ul class="mc-list">
          {#each activeAgentCards as { agent, card } (agent.agent_id)}
            <li class="mc-row">
              <span class="mc-dot running-dot" aria-hidden="true"></span>
              <span class="mc-card-id">{card.human_id ?? card.id.slice(-4)}</span>
              <span class="mc-card-title">{card.title.length > 50 ? card.title.slice(0, 50) + '…' : card.title}</span>
              <span class="mc-elapsed">{ovElapsed(agent.started_at)}</span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    {#if overdueCards.length > 0}
      <section class="ov-card mc-section">
        <h2>Overdue <span class="mc-count overdue">{overdueCards.length}</span></h2>
        <ul class="mc-list">
          {#each overdueCards as card (card.id)}
            <li class="mc-row">
              <span class="mc-dot overdue-dot" aria-hidden="true"></span>
              <span class="mc-card-id">{card.human_id ?? card.id.slice(-4)}</span>
              <span class="mc-card-title">{card.title.length > 50 ? card.title.slice(0, 50) + '…' : card.title}</span>
              <span class="mc-elapsed overdue-date">{card.due_date ? new Date(card.due_date).toLocaleDateString() : ''}</span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    <!-- Status breakdown -->
    {#if statusCounts.length > 0}
      <section class="ov-card">
        <h2>Status breakdown</h2>
        <div class="status-bars">
          {#each statusCounts as sc (sc.name)}
            <div class="status-bar-row">
              <StatusIcon status={sc.name} size={13} />
              <span class="bar-label">{sc.name}</span>
              <div class="bar-track">
                <div class="bar-fill" style="width: {totalCards ? (sc.count / totalCards * 100) : 0}%; background: {sc.color};"></div>
              </div>
              <span class="bar-count">{sc.count}</span>
            </div>
          {/each}
        </div>
      </section>
    {/if}

    <!-- Milestones -->
    <section class="ov-card">
      <h2>Milestones</h2>
      {#if milestones.length === 0}
        <p class="ov-empty">No milestones yet.</p>
      {:else}
        <ul class="milestone-list">
          {#each milestones as m (m.id)}
            <li class="milestone-row">
              <span class="ms-status" class:closed={m.status === 'closed'}>{m.status === 'closed' ? '✓' : '○'}</span>
              <span class="ms-title">{m.title}</span>
              {#if m.due_date}<span class="ms-due">{shortDate(m.due_date)}</span>{/if}
            </li>
          {/each}
        </ul>
      {/if}
    </section>

    <!-- Goals -->
    {#if goals.length > 0}
      <section class="ov-card">
        <h2>Workspace goals</h2>
        <ul class="goal-list">
          {#each goals as g (g.id)}
            <li class="goal-row">
              <span class="goal-title">{g.title}</span>
              {#if g.description}<span class="goal-desc">{g.description}</span>{/if}
            </li>
          {/each}
        </ul>
      </section>
    {/if}
  </div>
{/if}

<style>
  .overview {
    flex: 1;
    overflow-y: auto;
    padding: 2rem 2.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 820px;
    color: oklch(0.85 0.05 310 / 0.85);
  }
  .ov-header h1 {
    margin: 0 0 0.25rem;
    font-size: 1.5rem;
    font-weight: 600;
    color: oklch(0.94 0.08 310);
  }
  .ov-desc {
    margin: 0 0 0.5rem;
    font-size: 0.85rem;
    color: oklch(0.72 0.05 310 / 0.75);
    line-height: 1.55;
  }
  .ov-meta {
    display: flex;
    gap: 1.25rem;
    font-size: 0.72rem;
    color: oklch(0.55 0.04 310 / 0.60);
  }
  .meta-item code {
    font-family: 'JetBrains Mono', monospace;
    background: oklch(0.22 0.03 275 / 0.5);
    padding: 0.05rem 0.3rem;
    border-radius: 2px;
  }

  .ov-card {
    background: oklch(0.11 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 6px;
    padding: 1rem 1.25rem;
  }
  .ov-card h2 {
    margin: 0 0 0.65rem;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: oklch(0.65 0.06 310 / 0.65);
  }
  .mc-section h2 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .mc-count {
    background: oklch(0.72 0.28 310 / 0.15);
    color: oklch(0.80 0.26 310);
    border-radius: 10px;
    padding: 0.1rem 0.45rem;
    font-size: 0.68rem;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 400;
  }
  .mc-count.overdue {
    background: oklch(0.72 0.24 25 / 0.15);
    color: oklch(0.72 0.24 25);
  }
  .mc-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .mc-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.5rem;
    border-radius: 5px;
    transition: background 80ms ease;
  }
  .mc-row:hover { background: oklch(0.72 0.28 310 / 0.06); }
  .mc-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .running-dot {
    background: oklch(0.72 0.19 145);
    box-shadow: 0 0 5px oklch(0.72 0.19 145 / 0.55);
    animation: run-pulse 1.5s ease-in-out infinite;
  }
  @keyframes run-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.45; }
  }
  .overdue-dot { background: oklch(0.72 0.24 25); }
  .mc-card-id {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: oklch(0.72 0.28 310 / 0.65);
    flex-shrink: 0;
    width: 4.5rem;
  }
  .mc-card-title {
    flex: 1;
    font-size: 0.78rem;
    color: oklch(0.82 0.05 310 / 0.85);
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .mc-elapsed {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: oklch(0.55 0.04 310 / 0.60);
    flex-shrink: 0;
  }
  .overdue-date { color: oklch(0.72 0.24 25 / 0.75); }
  .ov-empty {
    margin: 0;
    font-size: 0.78rem;
    color: oklch(0.55 0.04 310 / 0.50);
    font-style: italic;
  }

  /* Status bars */
  .status-bars { display: flex; flex-direction: column; gap: 0.45rem; }
  .status-bar-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.78rem;
  }
  .bar-label {
    width: 90px;
    color: oklch(0.82 0.05 310 / 0.80);
  }
  .bar-track {
    flex: 1;
    height: 6px;
    background: oklch(0.17 0.025 275);
    border-radius: 3px;
    overflow: hidden;
  }
  .bar-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 200ms ease;
  }
  .bar-count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.68rem;
    color: oklch(0.65 0.04 310 / 0.60);
    min-width: 2rem;
    text-align: right;
  }

  /* Milestones */
  .milestone-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.35rem; }
  .milestone-row { display: flex; align-items: center; gap: 0.5rem; font-size: 0.78rem; }
  .ms-status { font-size: 0.9rem; color: oklch(0.55 0.04 310 / 0.50); }
  .ms-status.closed { color: oklch(0.78 0.16 150); }
  .ms-title { color: oklch(0.88 0.05 310); flex: 1; }
  .ms-due { font-family: 'JetBrains Mono', monospace; font-size: 0.68rem; color: oklch(0.55 0.04 310 / 0.55); }

  /* Goals */
  .goal-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.35rem; }
  .goal-row { display: flex; flex-direction: column; gap: 0.1rem; }
  .goal-title { font-size: 0.78rem; font-weight: 500; color: oklch(0.88 0.05 310); }
  .goal-desc { font-size: 0.7rem; color: oklch(0.60 0.05 310 / 0.60); }
</style>
